import { Suspense, lazy } from 'react';
import type { Exposure, Property, PropertyInput } from '@aimmo/contracts';
import {
  ENERGY_CLASSES,
  EXPOSURES,
  EXPOSURE_LABELS,
  FLOOR_LEVELS,
  FLOOR_LEVEL_LABELS,
  HEATINGS,
  HEATING_LABELS,
  OCCUPIED_BY,
  OCCUPIED_BY_LABELS,
  PROPERTY_STATUSES,
  PROPERTY_STATUS_LABELS,
  PROPERTY_USES,
  PROPERTY_KNOWN_ATTRIBUTE_KEYS,
  PROPERTY_USE_LABELS,
  enumOptions,
  propertyCreateSchema,
} from '@aimmo/contracts';
import { Link } from '@tanstack/react-router';
import { FileSignature } from 'lucide-react';
import { Badge, Button, defineResource, tsRestResourceClient } from '@aimmo/ui';
import { api } from '../lib/api.js';
import { resolveComune, searchComuni } from './geo-lookups.js';
import {
  resolveBuilding,
  resolveTypology,
  resolveZone,
  searchBuildings,
  searchTypologies,
  searchZones,
} from './property-lookups.js';
import { AddressSearchField } from './property-address-search.js';
import { AttributesField } from './property-attributes-field.js';
import {
  KNOWN_ATTRIBUTE_FIELDS,
  attributeEntries,
  formatAttribute,
} from './property-attributes.js';
import { MediaField } from './property-media-field.js';
import { PropertyMandates } from './property-mandates.js';
import { PropertyDemands } from './demand-matches.js';
import { ActivityTimeline } from '../features/activity-timeline.js';
import { formatPrice, streetLine } from '../lib/format.js';
/**
 * MapLibre is the largest dependency in the app and only the property form uses
 * it, so it is fetched when that form opens rather than on every page load.
 */
const PropertyMapField = lazy(() =>
  import('./property-map-field.js').then((module) => ({ default: module.PropertyMapField })),
);

/** Same chunk, so opening a detail page with a position pays for MapLibre once. */
const PropertyMapView = lazy(() =>
  import('./property-map-field.js').then((module) => ({ default: module.PropertyMapView })),
);

const STATUS_OPTIONS = enumOptions(PROPERTY_STATUSES, PROPERTY_STATUS_LABELS);
const USE_OPTIONS = enumOptions(PROPERTY_USES, PROPERTY_USE_LABELS);
const OCCUPIED_OPTIONS = enumOptions(OCCUPIED_BY, OCCUPIED_BY_LABELS);
const FLOOR_LEVEL_OPTIONS = enumOptions(FLOOR_LEVELS, FLOOR_LEVEL_LABELS);
const HEATING_OPTIONS = enumOptions(HEATINGS, HEATING_LABELS);
const EXPOSURE_OPTIONS = enumOptions(EXPOSURES, EXPOSURE_LABELS);
const ENERGY_OPTIONS = enumOptions(ENERGY_CLASSES);

const STATUS_TONES = {
  available: 'success',
  under_offer: 'warning',
  sold: 'neutral',
  rented: 'neutral',
} as const;

/**
 * The asset register (ADR-0005): typed columns for what agents filter on, a
 * key/value bag for the rest, media in object storage and a point on a map.
 *
 * The asking price is deliberately absent from this form — a property's
 * commercial terms belong to its mandate (M4). `price` here is the indicative
 * value the asset search runs on.
 */
export const propertiesResource = defineResource<
  Property,
  PropertyInput,
  PropertyInput,
  PropertyInput
>({
  key: 'properties',
  label: { one: 'Immobile', many: 'Immobili', gender: 'm' },
  client: tsRestResourceClient(api.properties),
  getRowId: (row) => row.id,
  defaultState: { sort: 'createdAt', order: 'desc' },
  search: { placeholder: 'Indirizzo, comune, tipologia, dati catastali…' },
  columns: [
    { id: 'displayLabel', header: 'Immobile' },
    {
      id: 'status',
      header: 'Stato',
      width: '10rem',
      cell: ({ row }) => (
        <Badge tone={STATUS_TONES[row.status]}>{PROPERTY_STATUS_LABELS[row.status]}</Badge>
      ),
    },
    {
      id: 'price',
      header: 'Valore',
      sortKey: 'price',
      align: 'right',
      width: '9rem',
      accessor: (row) => formatPrice(row.price, ''),
    },
    {
      id: 'surfaceMq',
      header: 'mq',
      sortKey: 'surfaceMq',
      align: 'right',
      width: '6rem',
    },
    { id: 'locali', header: 'Locali', sortKey: 'locali', align: 'right', width: '6rem' },
    {
      id: 'zona',
      header: 'Zona',
      accessor: (row) => row.zonaName ?? '',
      hiddenByDefault: true,
    },
    { id: 'buildingName', header: 'Edificio', hiddenByDefault: true },
    {
      id: 'media',
      header: 'Media',
      width: '6rem',
      align: 'right',
      accessor: (row) => row.media.length,
    },
  ],
  filters: [
    { id: 'status', label: 'Stato', type: 'enum', options: STATUS_OPTIONS },
    { id: 'use', label: 'Utilizzo', type: 'enum', options: USE_OPTIONS },
    {
      id: 'typologyId',
      label: 'Tipologia',
      type: 'relation',
      search: searchTypologies,
      resolve: resolveTypology,
    },
    {
      id: 'comuneId',
      label: 'Comune',
      type: 'relation',
      search: searchComuni,
      resolve: resolveComune,
    },
    { id: 'zonaId', label: 'Zona', type: 'relation', search: searchZones, resolve: resolveZone },
    {
      id: 'buildingId',
      label: 'Edificio',
      type: 'relation',
      search: searchBuildings,
      resolve: resolveBuilding,
    },
    // Ranges are two flat parameters rather than one control: the API reads
    // them as scalars, and each half is independently clearable.
    { id: 'priceMin', label: 'Prezzo da', type: 'number', min: 0, step: 10_000 },
    { id: 'priceMax', label: 'Prezzo a', type: 'number', min: 0, step: 10_000 },
    { id: 'surfaceMin', label: 'mq da', type: 'number', min: 0, step: 10 },
    { id: 'surfaceMax', label: 'mq a', type: 'number', min: 0, step: 10 },
    { id: 'localiMin', label: 'Locali da', type: 'number', min: 0, step: 1 },
    { id: 'localiMax', label: 'Locali a', type: 'number', min: 0, step: 1 },
    { id: 'prestigio', label: 'Prestigio', type: 'boolean' },
  ],
  form: {
    schema: propertyCreateSchema,
    defaultValues: {
      buildingId: '',
      typologyId: '',
      use: 'residential',
      status: 'available',
      occupiedBy: 'free',
      price: '',
      surfaceMq: null,
      surfaceWalkableMq: null,
      locali: null,
      bagni: null,
      floor: null,
      floorLevel: '',
      energyClass: '',
      heating: '',
      exposure: [] as Exposure[],
      addressStreet: '',
      addressNumber: '',
      addressPostalCode: '',
      comuneId: '',
      zonaId: '',
      lat: null,
      lng: null,
      catastoFoglio: '',
      catastoParticella: '',
      catastoSubalterno: '',
      catastoCategoria: '',
      prestigio: false,
      attributes: {},
      media: [],
    },
    fromItem: (property) => {
      return {
        buildingId: property.buildingId ?? '',
        typologyId: property.typologyId ?? '',
        use: property.use,
        status: property.status,
        occupiedBy: property.occupiedBy,
        price: property.price ?? '',
        surfaceMq: property.surfaceMq,
        surfaceWalkableMq: property.surfaceWalkableMq,
        locali: property.locali,
        bagni: property.bagni,
        floor: property.floor,
        floorLevel: property.floorLevel ?? '',
        energyClass: property.energyClass ?? '',
        heating: property.heating ?? '',
        exposure: property.exposure,
        addressStreet: property.addressStreet ?? '',
        addressNumber: property.addressNumber ?? '',
        addressPostalCode: property.addressPostalCode ?? '',
        comuneId: property.comuneId ?? '',
        zonaId: property.zonaId ?? '',
        lat: property.lat,
        lng: property.lng,
        catastoFoglio: property.catastoFoglio ?? '',
        catastoParticella: property.catastoParticella ?? '',
        catastoSubalterno: property.catastoSubalterno ?? '',
        catastoCategoria: property.catastoCategoria ?? '',
        prestigio: property.prestigio,
        attributes: property.attributes as PropertyInput['attributes'],
        // The wire carries an id and a signed URL per file; the form carries
        // what the API accepts back, which is the key and its metadata.
        media: property.media.map((file) => ({
          path: file.path,
          role: file.role,
          fileName: file.fileName,
          contentType: file.contentType,
          sizeBytes: file.sizeBytes,
          caption: file.caption,
        })),
      };
    },
    sections: [
      {
        id: 'classification',
        title: 'Classificazione',
        fields: [
          {
            name: 'typologyId',
            label: 'Tipologia',
            type: 'relation',
            search: searchTypologies,
            resolve: resolveTypology,
          },
          { name: 'use', label: 'Utilizzo', type: 'select', required: true, options: USE_OPTIONS },
          {
            name: 'status',
            label: 'Stato',
            type: 'select',
            required: true,
            options: STATUS_OPTIONS,
          },
          {
            name: 'occupiedBy',
            label: 'Occupato da',
            type: 'select',
            required: true,
            options: OCCUPIED_OPTIONS,
          },
          { name: 'prestigio', label: 'Immobile di prestigio', type: 'switch' },
        ],
      },
      {
        id: 'size',
        title: 'Consistenza',
        description: 'Il prezzo qui è indicativo: quello di vendita vive sull’incarico.',
        fields: [
          { name: 'price', label: 'Valore indicativo (€)', type: 'text', placeholder: '285000.00' },
          { name: 'surfaceMq', label: 'Superficie lorda (mq)', type: 'number', min: 0 },
          {
            name: 'surfaceWalkableMq',
            label: 'Superficie calpestabile (mq)',
            type: 'number',
            min: 0,
          },
          { name: 'locali', label: 'Locali', type: 'number', min: 0 },
          { name: 'bagni', label: 'Bagni', type: 'number', min: 0 },
          { name: 'floor', label: 'Piano', type: 'number', min: -5 },
          { name: 'floorLevel', label: 'Livello', type: 'select', options: FLOOR_LEVEL_OPTIONS },
          {
            name: 'energyClass',
            label: 'Classe energetica',
            type: 'select',
            options: ENERGY_OPTIONS,
          },
          { name: 'heating', label: 'Riscaldamento', type: 'select', options: HEATING_OPTIONS },
          {
            name: 'exposure',
            label: 'Esposizione',
            type: 'multiSelect',
            options: EXPOSURE_OPTIONS,
            wide: true,
          },
        ],
      },
      {
        id: 'address',
        title: 'Indirizzo',
        columns: 3,
        // Not a field: it owns no name and writes six siblings — street,
        // number, CAP, comune and the two coordinates — all of which stay
        // editable afterwards.
        renderBefore: () => <AddressSearchField />,
        fields: [
          { name: 'addressStreet', label: 'Via', type: 'text' },
          { name: 'addressNumber', label: 'Civico', type: 'text' },
          { name: 'addressPostalCode', label: 'CAP', type: 'text', placeholder: '20121' },
          {
            name: 'comuneId',
            label: 'Comune',
            type: 'relation',
            search: searchComuni,
            resolve: resolveComune,
          },
          {
            name: 'zonaId',
            label: 'Zona',
            type: 'relation',
            search: searchZones,
            resolve: resolveZone,
          },
          {
            name: 'buildingId',
            label: 'Edificio',
            type: 'relation',
            search: searchBuildings,
            resolve: resolveBuilding,
          },
        ],
      },
      {
        id: 'position',
        title: 'Posizione',
        description: 'Clicca la mappa o trascina il segnaposto. Alimenta la ricerca per raggio.',
        columns: 1,
        fields: [
          {
            name: 'lat',
            label: 'Posizione',
            type: 'custom',
            wide: true,
            render: (props) => (
              <Suspense
                fallback={
                  <div className="h-72 w-full animate-pulse rounded-card border border-border bg-surface-sunken" />
                }
              >
                <PropertyMapField
                  value={props.value}
                  onChange={(next) => props.onChange(next)}
                  disabled={props.disabled}
                />
              </Suspense>
            ),
          },
          // No `lng` field: the map above *is* the position control and writes
          // both values through the form context. A disabled number input next
          // to it showed a longitude with no latitude, which is what the UAT
          // pass found (docs/milestones/U2-detail-form-layout.md).
        ],
      },
      {
        id: 'catasto',
        title: 'Dati catastali',
        fields: [
          { name: 'catastoFoglio', label: 'Foglio', type: 'text' },
          { name: 'catastoParticella', label: 'Particella', type: 'text' },
          { name: 'catastoSubalterno', label: 'Subalterno', type: 'text' },
          { name: 'catastoCategoria', label: 'Categoria', type: 'text', placeholder: 'A/2' },
        ],
      },
      {
        // The legacy taxonomies that kept a closed vocabulary but never earned
        // a column (ADR-0005): they live in the attribute bag, under the keys
        // the contract fixes, and are asked here as ordinary selects.
        id: 'features',
        title: 'Caratteristiche',
        columns: 3,
        fields: KNOWN_ATTRIBUTE_FIELDS,
      },
      {
        id: 'attributes',
        title: 'Altri attributi',
        description: 'Ciò che non ha una colonna dedicata e non serve filtrare.',
        columns: 1,
        fields: [
          {
            name: 'attributes',
            label: 'Attributi',
            type: 'custom',
            wide: true,
            render: (props) => (
              <AttributesField
                value={props.value}
                onChange={(next) => props.onChange(next)}
                disabled={props.disabled}
                reserved={PROPERTY_KNOWN_ATTRIBUTE_KEYS}
                name={props.name}
              />
            ),
          },
        ],
      },
      {
        id: 'media',
        title: 'Media',
        columns: 1,
        fields: [
          {
            name: 'media',
            label: 'File',
            type: 'custom',
            wide: true,
            render: (props) => (
              <MediaField
                value={props.value}
                onChange={(next) => props.onChange(next)}
                disabled={props.disabled}
              />
            ),
          },
        ],
      },
    ],
  },
  detail: {
    title: (row) => row.displayLabel,
    // The action someone came to the page for, next to Modifica and before
    // Elimina. A sold property is not one anybody writes a new mandate over.
    actions: (row) =>
      row.status === 'sold' || row.status === 'rented' ? null : (
        <Button asChild variant="secondary" size="sm">
          <Link to="/incarichi/nuovo" search={{ propertyId: row.id }}>
            <FileSignature />
            Crea incarico
          </Link>
        </Button>
      ),
    // The commercial history of the asset, and who is looking for something
    // like it, in the slot the kit keeps for exactly this (the activity
    // timeline lands here too, in M6).
    after: (row) => (
      <div className="grid gap-[var(--card-gap)]">
        <PropertyMandates propertyId={row.id} />
        <ActivityTimeline subjectType="property" subjectId={row.id} />
        {/* Last on the page: who is looking for something like this is context,
            not the work of the day. */}
        <PropertyDemands propertyId={row.id} />
      </div>
    ),
    sections: [
      {
        id: 'main',
        title: 'Scheda',
        columns: 3,
        fields: [
          {
            id: 'status',
            label: 'Stato',
            render: (row) => (
              <Badge tone={STATUS_TONES[row.status]}>{PROPERTY_STATUS_LABELS[row.status]}</Badge>
            ),
          },
          { id: 'use', label: 'Utilizzo', accessor: (row) => PROPERTY_USE_LABELS[row.use] },
          {
            id: 'occupiedBy',
            label: 'Occupato da',
            accessor: (row) => OCCUPIED_BY_LABELS[row.occupiedBy],
          },
          { id: 'typologyLabel', label: 'Tipologia' },
          {
            id: 'price',
            label: 'Valore indicativo',
            accessor: (row) => formatPrice(row.price),
          },
          { id: 'surfaceMq', label: 'Superficie (mq)' },
          { id: 'locali', label: 'Locali' },
          { id: 'bagni', label: 'Bagni' },
          { id: 'floor', label: 'Piano' },
          {
            id: 'energyClass',
            label: 'Classe energetica',
            accessor: (row) => row.energyClass ?? '—',
          },
          {
            id: 'exposure',
            label: 'Esposizione',
            accessor: (row) =>
              row.exposure.length === 0
                ? '—'
                : row.exposure.map((value) => EXPOSURE_LABELS[value]).join(', '),
          },
          { id: 'prestigio', label: 'Prestigio', accessor: (row) => (row.prestigio ? 'Sì' : 'No') },
        ],
      },
      {
        id: 'where',
        title: 'Indirizzo',
        columns: 3,
        renderAfter: (row) =>
          row.lat === null || row.lng === null ? null : (
            <Suspense
              fallback={
                <div className="h-64 w-full animate-pulse rounded-card border border-border bg-surface-sunken" />
              }
            >
              <PropertyMapView lat={row.lat} lng={row.lng} />
            </Suspense>
          ),
        fields: [
          {
            id: 'address',
            label: 'Indirizzo',
            accessor: (row) => streetLine(row.addressStreet, row.addressNumber) || '—',
          },
          {
            id: 'comune',
            label: 'Comune',
            accessor: (row) => (row.comuneName ? `${row.comuneName} (${row.sigla})` : '—'),
          },
          { id: 'zonaName', label: 'Zona' },
          { id: 'buildingName', label: 'Edificio' },
          {
            id: 'position',
            label: 'Coordinate',
            accessor: (row) =>
              row.lat !== null && row.lng !== null
                ? `${row.lat.toFixed(6)}, ${row.lng.toFixed(6)}`
                : '—',
          },
        ],
      },
      {
        id: 'catasto',
        title: 'Catasto',
        columns: 4,
        collapsible: true,
        defaultCollapsed: true,
        fields: [
          { id: 'catastoFoglio', label: 'Foglio' },
          { id: 'catastoParticella', label: 'Particella' },
          { id: 'catastoSubalterno', label: 'Subalterno' },
          { id: 'catastoCategoria', label: 'Categoria' },
        ],
      },
      {
        id: 'attributes',
        title: 'Caratteristiche',
        render: (row) => {
          const entries = attributeEntries(row.attributes);
          if (entries.length === 0) return <p className="text-secondary text-muted">Nessuno.</p>;

          return (
            <dl className="grid gap-2 sm:grid-cols-2">
              {entries.map(([key, value]) => {
                const shown = formatAttribute(key, value);
                return (
                  <div key={key} className="flex justify-between gap-4 border-b border-border pb-1">
                    <dt className="text-secondary text-muted">{shown.label}</dt>
                    <dd className="text-body text-ink">{shown.value}</dd>
                  </div>
                );
              })}
            </dl>
          );
        },
      },
      {
        id: 'media',
        title: 'Media',
        render: (row) => {
          if (row.media.length === 0) {
            return <p className="text-secondary text-muted">Nessun file.</p>;
          }

          return (
            <ul className="grid gap-3 sm:grid-cols-3">
              {row.media.map((file) => (
                <li key={file.id} className="grid gap-1">
                  {file.contentType.startsWith('image/') ? (
                    <img
                      src={file.url}
                      alt={file.caption ?? file.fileName}
                      loading="lazy"
                      className="aspect-[4/3] w-full rounded-card object-cover"
                    />
                  ) : (
                    <a
                      href={file.url}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-card border border-border px-3 py-6 text-center text-body text-ink-2 hover:bg-surface-sunken"
                    >
                      {file.fileName}
                    </a>
                  )}
                  <span className="text-secondary text-muted">{file.caption ?? file.fileName}</span>
                </li>
              ))}
            </ul>
          );
        },
      },
    ],
  },
  empty: {
    title: 'Nessun immobile',
    description: 'Il registro degli asset. Registra un immobile per iniziare.',
  },
});
